import React from 'react';

import PropTypes from 'prop-types';
import cn from 'classnames';
import {
  HiChevronDown,
  HiStar,
  HiInformationCircle,
  HiDotsVertical,
  HiArrowSmUp,
  HiArrowSmDown,
  HiOutlinePencilAlt,
} from 'react-icons/hi';
import { IoClose } from 'react-icons/io5';

import {
  Image,
  Checkbox,
  Text,
  Avatar,
  Indicator,
  Badge,
  Button,
  FormInput,
  IconButton,
  Menu,
  FormInputNumber,
} from 'modules/common';

function TableCell({
  type,
  leadingItem,
  label,
  trailingItem,
  badgeProps,
  percentageBadgeProps,
  textProps,
  avatarProps,
  indicatorProps,
  iconProps,
  checkboxProps,
  inputProps,
  iconButtonProps,
  menuProps,
  actionProps: $actionProps,
  className,
  buttonProps,
  imageProps,
}) {
  const actionProps = {
    withEdit: false,
    withRemove: false,
    withMenu: false,
    onEdit: () => {},
    onRemove: () => {},
    menuProps: {},
    ...$actionProps,
  };

  const actionRef = React.useRef();
  const [isActionOpen, setIsActionOpen] = React.useState(false);

  const dropdownRef = React.useRef();
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);

  const [isEditableOpen, setIsEditableOpen] = React.useState(false);

  const dispatchType = {
    text: <Text className="flex-1">{label}</Text>,
    avatar: (
      <div className="flex items-center gap-ds-12">
        <div>
          <Avatar size="32px" radius="rounded" {...avatarProps} />
        </div>
        <Text {...textProps}>{label}</Text>
      </div>
    ),
    indicator: (
      <div className="flex items-center gap-ds-8">
        <Indicator type="success" {...indicatorProps} />
        <Text {...textProps}>{label}</Text>
      </div>
    ),
    info: (
      <div className="flex items-center gap-ds-8 text-brand-primary-base">
        <HiInformationCircle
          {...iconProps}
          className={cn('h-ds-16 w-ds-16', iconProps?.className)}
        />
        <Text
          type="caption"
          {...textProps}
          className={cn(textProps?.className)}
          weight="semibold"
        >
          {label}
        </Text>
      </div>
    ),
    'info-black': (
      <div className="flex items-center gap-ds-8 text-brand-primary-base">
        <HiInformationCircle
          {...iconProps}
          className={cn('h-ds-16 w-ds-16 text-muted', iconProps?.className)}
        />
        <Text
          type="body"
          {...textProps}
          className={cn(
            'text-primitive-black font-normal',
            textProps?.className
          )}
          weight="semibold"
        >
          {label}
        </Text>
      </div>
    ),
    editable: !isEditableOpen ? (
      <div className="flex items-center gap-ds-6 text-brand-primary-base">
        <Text
          type="body"
          {...textProps}
          className={cn(
            'text-primitive-black font-normal',
            textProps?.className
          )}
          weight="semibold"
        >
          {label}
        </Text>

        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="17"
          height="16"
          viewBox="0 0 17 16"
          fill="none"
          {...iconProps}
          className={cn('cursor-pointer', iconProps?.className)}
          onClick={() => {
            setIsEditableOpen(!isEditableOpen);
          }}
        >
          <path
            d="M7.83332 3.33335H4.49999C3.76361 3.33335 3.16666 3.93031 3.16666 4.66669V12C3.16666 12.7364 3.76361 13.3334 4.49999 13.3334H11.8333C12.5697 13.3334 13.1667 12.7364 13.1667 12V8.66669M12.2238 2.39054C12.7445 1.86984 13.5888 1.86984 14.1095 2.39054C14.6302 2.91124 14.6302 3.75546 14.1095 4.27616L8.38561 10H6.49999L6.49999 8.1144L12.2238 2.39054Z"
            stroke="#606774"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    ) : (
      <div className="flex items-center gap-ds-4">
        <FormInput
          label={false}
          icon={({ buttonProps: $buttonProps, solidButtonClassName }) => (
            <Button
              label="Add"
              className={cn('ds-primary', solidButtonClassName)}
              {...$buttonProps}
            />
          )}
          {...inputProps}
          className={cn('pr-16', inputProps?.className)}
        />

        <div>
          <IconButton
            icon={() => <IoClose className="h-[18px] w-[18px]" />}
            {...iconButtonProps}
            onClick={() => {
              setIsEditableOpen(false);
            }}
          />
        </div>
      </div>
    ),
    badge: (
      <Badge label={label} radius="rounded" withClose={false} {...badgeProps} />
    ),
    'percentage-badge': (
      <div className="flex items-center gap-ds-8">
        <Text {...textProps}>{label}</Text>

        <Badge
          leadingItem={
            percentageBadgeProps?.isArrowUp ? (
              <HiArrowSmUp className="h-3.5 w-3.5" />
            ) : (
              <HiArrowSmDown className="h-3.5 w-3.5" />
            )
          }
          type={percentageBadgeProps?.isArrowUp ? 'success' : 'danger'}
          radius="rounded"
          withClose={false}
          {...percentageBadgeProps}
        />
      </div>
    ),
    rating: (
      <div className="flex items-center gap-ds-8">
        <HiStar
          {...iconProps}
          className={cn(
            'h-ds-20 w-ds-20 text-utility-warning-base',
            iconProps?.className
          )}
        />
        <Text {...textProps}>{label}</Text>
      </div>
    ),
    checkbox: <Checkbox {...checkboxProps} />,
    dropdown: (
      <div
        className="flex items-center gap-ds-4 cursor-pointer relative"
        ref={dropdownRef}
        onClick={() => {
          setIsDropdownOpen(!isDropdownOpen);
        }}
        role="presentation"
      >
        <Text {...textProps}>{label}</Text>

        <div>
          <HiChevronDown
            {...iconProps}
            className={cn('w-5 h-5 text-muted', iconProps?.className, {
              'rotate-180': isDropdownOpen,
            })}
          />

          <Menu
            width="200px"
            isOpen={isDropdownOpen}
            clickOutsideProps={{
              ref: dropdownRef,
              set: setIsDropdownOpen,
            }}
            {...menuProps}
            className={cn('right-0 mt-1', menuProps?.className)}
            onChange={(value) => {
              if (typeof menuProps?.onChange !== 'function') {
                return;
              }

              menuProps?.onChange(value);
            }}
          />
        </div>
      </div>
    ),
    image: (
      <div className="flex items-center gap-ds-12">
        <div>
          <Image
            size="32px"
            defaultImageClassName="!h-5 !w-5 !text-placeholder"
            containerClassName="!border"
            {...imageProps}
          />
        </div>
        <Text {...textProps}>{label}</Text>
      </div>
    ),
    button: <Button type="primary" variant="text" {...buttonProps} />,
    input:
      inputProps?.type === 'number' ? (
        <FormInputNumber
          label={false}
          icon={({ buttonProps: $buttonProps, solidButtonClassName }) => (
            <Button
              label="Add"
              className={cn('ds-primary', solidButtonClassName)}
              {...$buttonProps}
            />
          )}
          {...inputProps}
          className={cn(inputProps?.className)}
        />
      ) : (
        <FormInput
          label={false}
          icon={({ buttonProps: $buttonProps, solidButtonClassName }) => (
            <Button
              label="Add"
              className={cn('ds-primary', solidButtonClassName)}
              {...$buttonProps}
            />
          )}
          {...inputProps}
          className={cn(inputProps?.className)}
        />
      ),
    action: (
      <div className="flex items-center gap-ds-4">
        {actionProps?.withEdit && (
          <IconButton
            icon={<HiOutlinePencilAlt className="h-ds-16 w-ds-16" />}
            size="sm"
            {...iconButtonProps}
            {...actionProps?.editProps}
            onClick={actionProps?.onEdit}
          />
        )}

        {actionProps?.withRemove && (
          <IconButton
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M12.6667 4.66672L12.0885 12.7617C12.0387 13.4595 11.4581 14.0001 10.7586 14.0001H5.24157C4.54205 14.0001 3.96147 13.4595 3.91163 12.7617L3.33341 4.66672M6.66675 7.33339V11.3334M9.33341 7.33339V11.3334M10.0001 4.66672V2.66671C10.0001 2.29852 9.7016 2.00005 9.33341 2.00005H6.66675C6.29856 2.00005 6.00008 2.29852 6.00008 2.66671V4.66672M2.66675 4.66672H13.3334"
                  stroke="#DB4343"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            }
            size="sm"
            {...iconButtonProps}
            {...actionProps?.removeProps}
            onClick={(e) => {
              e.stopPropagation();
              actionProps?.onRemove();
            }}
          />
        )}

        {actionProps?.withMenu && (
          <div className="relative" ref={actionRef}>
            <IconButton
              icon={<HiDotsVertical className="h-3.5 w-3.5" />}
              size="sm"
              {...iconButtonProps}
              {...actionProps?.dotsProps}
              onClick={() => {
                setIsActionOpen(!isActionOpen);
              }}
            />

            <Menu
              width="200px"
              isOpen={isActionOpen}
              clickOutsideProps={{
                ref: actionRef,
                set: setIsActionOpen,
              }}
              emptyStateProps={{
                description: '',
              }}
              {...menuProps}
              className={cn('right-0 mt-1', menuProps?.className)}
              onChange={(value) => {
                if (typeof menuProps?.onChange !== 'function') {
                  return;
                }

                menuProps?.onChange(value);
              }}
            />
          </div>
        )}
      </div>
    ),
  };

  return (
    <div className={cn('flex items-center gap-ds-4', className)}>
      {leadingItem && leadingItem}
      {dispatchType?.[type]}
      {trailingItem && trailingItem}
    </div>
  );
}

TableCell.defaultProps = {
  type: 'text',
  leadingItem: null,
  label: '',
  trailingItem: null,
  badgeProps: {},
  textProps: {},
  avatarProps: {},
  indicatorProps: {},
  iconProps: {},
  checkboxProps: {},
  inputProps: {},
  iconButtonProps: {},
  actionProps: {},
  menuProps: {},
  percentageBadgeProps: {},
  buttonProps: {},
  imageProps: {},
  className: '',
};

TableCell.propTypes = {
  type: PropTypes.oneOf([
    'text',
    'avatar',
    'indicator',
    'info',
    'info-black',
    'editable',
    'badge',
    'percentage-badge',
    'rating',
    'checkbox',
    'dropdown',
    'image',
    'input',
    'button',
    'action',
  ]),
  className: PropTypes.string,
  leadingItem: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.element,
    PropTypes.number,
    PropTypes.string,
    PropTypes.instanceOf(Object),
    PropTypes.instanceOf(Function),
  ]),
  imageProps: PropTypes.instanceOf(Object),
  label: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.element,
    PropTypes.string,
    PropTypes.instanceOf(Object),
    PropTypes.instanceOf(Function),
  ]),
  iconButtonProps: PropTypes.instanceOf(Object),
  trailingItem: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.element,
    PropTypes.number,
    PropTypes.string,
    PropTypes.instanceOf(Object),
    PropTypes.instanceOf(Function),
  ]),
  badgeProps: PropTypes.instanceOf(Object),
  percentageBadgeProps: PropTypes.shape({
    // badge props extended
    isArrowUp: PropTypes.bool,
  }),
  menuProps: PropTypes.instanceOf(Object),
  textProps: PropTypes.instanceOf(Object),
  avatarProps: PropTypes.instanceOf(Object),
  indicatorProps: PropTypes.instanceOf(Object),
  iconProps: PropTypes.instanceOf(Object),
  checkboxProps: PropTypes.instanceOf(Object),
  inputProps: PropTypes.instanceOf(Object),
  buttonProps: PropTypes.instanceOf(Object),
  actionProps: PropTypes.shape({
    withEdit: PropTypes.bool,
    withRemove: PropTypes.bool,
    withMenu: PropTypes.bool,
    onEdit: PropTypes.instanceOf(Function),
    onRemove: PropTypes.instanceOf(Function),
    menuProps: PropTypes.instanceOf(Object),
  }),
};

export default TableCell;
